import type { Fetcher } from 'swr';
import useSWRInfinite from 'swr/infinite';

import { workQueueList } from '~/data/generated/endpoints/work-queue/work-queue';
import type { WorkQueueListParams } from '~/data/generated/model';
import { parseApiResponse } from '~/data/parseApiResponse';
import {
  type ActionItem,
  type ActionItemFilterParams,
  type ActionItemListResponse,
  actionItemListResponseSchema,
} from '~/data/schemas/actionItem';

const PAGE_SIZE = 50;

// Action items are created server-side by background processing (a document
// finishes extracting, an upload reaches split review) with no client event to
// react to. Poll so the list and the sidebar count go stale on their own rather
// than only on focus/navigation.
export const WORK_QUEUE_POLL_MS = 30_000;

// The app's filter params are camelCase; the generated work-queue query params
// are snake_case. Map them at the fetch boundary so callers keep the
// ActionItemFilterParams shape (AMP-503).
const toQueryParams = (params: ActionItemFilterParams): WorkQueueListParams => ({
  event_type: params.eventType,
  severity: params.severity,
  suggested_action: params.suggestedAction,
  portfolio: params.portfolio,
  property: params.property,
  car: params.car,
  region: params.region,
  include_snoozed: params.includeSnoozed,
  page: params.page,
  page_size: params.pageSize,
});

const actionItemsFetcher: Fetcher<
  ActionItemListResponse,
  readonly ['action-items', ActionItemFilterParams]
> = async ([_, params]) => {
  const { data } = await workQueueList(toQueryParams(params ?? {}));
  return parseApiResponse(actionItemListResponseSchema, data, 'ActionItems');
};

export function useActionItemsInfinite(
  params: ActionItemFilterParams = {},
  { enabled = true, refreshInterval }: { enabled?: boolean; refreshInterval?: number } = {},
) {
  const getKey = (
    pageIndex: number,
    previousPage: ActionItemListResponse | null,
  ): readonly ['action-items', ActionItemFilterParams] | null => {
    if (!enabled) return null;
    if (previousPage && !previousPage.next) return null;
    return [
      'action-items',
      { ...params, page: pageIndex + 1, pageSize: params.pageSize ?? PAGE_SIZE },
    ] as const;
  };

  const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite<
    ActionItemListResponse,
    Error,
    typeof getKey
  >(getKey, actionItemsFetcher, { revalidateFirstPage: true, refreshInterval });

  const pages = data ?? [];
  const actionItems: ActionItem[] = pages.flatMap((p) => p.results);
  const count = pages[0]?.count ?? 0;
  const hasMore = pages.length > 0 ? Boolean(pages[pages.length - 1]?.next) : false;
  const loadingMore = isValidating && size > 1;
  return {
    actionItems,
    count,
    hasMore,
    loadMore: () => setSize(size + 1),
    isLoading,
    isLoadingMore: loadingMore,
    isValidating,
    error,
    mutate,
  };
}
